// Browser helper: load any image File into a luminance grid for vase texture displacement.
// Handles JPEG/PNG/WebP/SVG/GIF via Image element + 2D canvas (max compatibility across browsers).
import type { TextureGrid } from '../core/vaseModel';
import { luminanceToSdf } from './sdf';

export async function imageToTextureGrid(
  file: File,
  intensity: number,
  repeatU: number,
  repeatV: number,
  maxSize = 256
): Promise<TextureGrid> {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const w = img.naturalWidth || 256;
    const h = img.naturalHeight || 256;
    const scale = Math.min(1, maxSize / Math.max(w, h));
    const outW = Math.max(8, Math.round(w * scale));
    const outH = Math.max(8, Math.round(h * scale));
    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2d context unavailable');
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, outW, outH);
    ctx.drawImage(img, 0, 0, outW, outH);
    const pix = ctx.getImageData(0, 0, outW, outH);
    const lum = new Float32Array(outW * outH);
    for (let i = 0; i < outW * outH; i++) {
      const r = pix.data[i * 4];
      const g = pix.data[i * 4 + 1];
      const b = pix.data[i * 4 + 2];
      lum[i] = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    }
    // Same SDF pipeline as svgToTextureGrid — gives uploaded raster images
    // crisp boundaries on the vase regardless of zoom or vase scale.
    const data = luminanceToSdf(lum, outW, outH);
    return { w: outW, h: outH, data, intensity, repeatU, repeatV };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image decode failed'));
    img.src = src;
  });
}
